import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Send, Users, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import usePushNotifications from '../../hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PushNotifications() {
  const token = localStorage.getItem('token');
  const { isSupported, permission, isSubscribed, error: pushError, subscribe, unsubscribe } = usePushNotifications(token);

  const [notification, setNotification] = useState({ title: '', body: '', url: '/' });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    const result = await subscribe();
    if (result) {
      setSuccess('Push-Benachrichtigungen aktiviert!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleUnsubscribe = async () => {
    const result = await unsubscribe();
    if (result) {
      setSuccess('Push-Benachrichtigungen deaktiviert!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleSendBroadcast = async () => {
    if (!notification.title || !notification.body) {
      setError('Titel und Nachricht sind erforderlich');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_URL}/api/push-notifications/admin/broadcast`,
        {
          title: notification.title,
          body: notification.body,
          url: notification.url,
          icon: '/logo192.png'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`Benachrichtigung an ${response.data.sent_count} Nutzer gesendet!`);
      setNotification({ title: '', body: '', url: '/' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Push-Benachrichtigungen</h1>
        <p className="text-gray-600 mt-2">
          Verwalten Sie Push-Benachrichtigungen für Ihre Kunden
        </p>
      </div>

      {/* Browser Support Status */}
      <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Browser-Unterstützung</h3>
            <p className="text-sm text-blue-700 mt-1">
              {isSupported
                ? 'Ihr Browser unterstützt Push-Benachrichtigungen ✓'
                : 'Ihr Browser unterstützt leider keine Push-Benachrichtigungen'}
            </p>
            {isSupported && (
              <p className="text-sm text-blue-700 mt-1">
                Berechtigung: <span className="font-semibold">{permission}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Admin Subscription Toggle */}
      {isSupported && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Bell size={20} />
                Admin Benachrichtigungen
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {isSubscribed
                  ? 'Sie erhalten Benachrichtigungen über neue Bestellungen und Nachrichten'
                  : 'Aktivieren Sie Benachrichtigungen für neue Bestellungen und Nachrichten'}
              </p>
            </div>
            <button
              onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isSubscribed
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isSubscribed ? (
                <span className="flex items-center gap-2">
                  <BellOff size={18} />
                  Deaktivieren
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Bell size={18} />
                  Aktivieren
                </span>
              )}
            </button>
          </div>
          {pushError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {pushError}
            </div>
          )}
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
          <CheckCircle size={20} />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Broadcast Notification Form */}
      <div className="bg-white rounded-lg shadow border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users size={20} />
          Benachrichtigung an alle senden
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titel *
            </label>
            <input
              type="text"
              value={notification.title}
              onChange={(e) => setNotification({ ...notification, title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. Neue Angebote!"
              maxLength={50}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nachricht *
            </label>
            <textarea
              value={notification.body}
              onChange={(e) => setNotification({ ...notification, body: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. Schauen Sie sich unsere neuesten Produkte an!"
              rows="3"
              maxLength={120}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link (optional)
            </label>
            <input
              type="text"
              value={notification.url}
              onChange={(e) => setNotification({ ...notification, url: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="/shop"
            />
            <p className="text-xs text-gray-500 mt-1">
              Wohin sollen Nutzer geleitet werden, wenn sie auf die Benachrichtigung klicken?
            </p>
          </div>

          <button
            onClick={handleSendBroadcast}
            disabled={sending || !isSupported}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Send size={18} />
            {sending ? 'Wird gesendet...' : 'Benachrichtigung senden'}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2">ℹ️ Hinweise</h3>
        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
          <li>Push-Benachrichtigungen funktionieren nur über HTTPS</li>
          <li>Nutzer müssen die Berechtigung explizit erteilen</li>
          <li>Automatische Benachrichtigungen werden bei neuen Bestellungen & Nachrichten an Admins gesendet</li>
          <li>Mobile Geräte zeigen Benachrichtigungen auch wenn die App geschlossen ist</li>
        </ul>
      </div>
    </div>
  );
}
